import React, { useEffect, useState } from "react";

const normalizeWhitespace = (value) => String(value || "").replace(/\s+/g, " ").trim();

const CompanyAttachActions = ({
  show = false,
  selectedRows = [],
  isAttaching = false,
  error = "",
  onSearchCompanies,
  onAttachCompany,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [localError, setLocalError] = useState("");

  const resetSelection = () => {
    setSelectedCompany(null);
    setShowConfirm(false);
  };

  const handleOpen = () => {
    setIsOpen(true);
    setLocalError("");
    resetSelection();
  };

  const handleClose = () => {
    setIsOpen(false);
    setSearchQuery("");
    setSearchResults([]);
    setIsSearching(false);
    setLocalError("");
    resetSelection();
  };

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const query = normalizeWhitespace(searchQuery);
    if (!query) {
      setSearchResults([]);
      setIsSearching(false);
      return undefined;
    }

    let isActive = true;
    setIsSearching(true);
    setLocalError("");

    const timeoutId = window.setTimeout(async () => {
      try {
        const results = await onSearchCompanies?.(query);
        if (!isActive) {
          return;
        }
        setSearchResults(Array.isArray(results) ? results : []);
        setSelectedCompany(null);
      } catch (searchError) {
        if (!isActive) {
          return;
        }
        setSearchResults([]);
        setLocalError(searchError?.message || "Unable to search companies right now.");
      } finally {
        if (isActive) {
          setIsSearching(false);
        }
      }
    }, 300);

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [isOpen, onSearchCompanies, searchQuery]);

  if (!show) {
    return null;
  }

  const handleAttach = async () => {
    if (!selectedCompany) {
      return;
    }

    setLocalError("");
    try {
      await onAttachCompany?.({
        selectedRows,
        companyRecord: selectedCompany,
      });
      handleClose();
    } catch (attachError) {
      setLocalError(attachError?.message || "Unable to attach the selected documents.");
    }
  };

  return (
    <div className="mt-4 rounded-2xl border border-sky-500/20 bg-slate-950/50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-slate-300">
          <span className="font-semibold text-slate-100">{selectedRows.length}</span> document
          {selectedRows.length === 1 ? "" : "s"} selected
        </p>
        {!isOpen ? (
          <button
            type="button"
            onClick={handleOpen}
            className="rounded-lg border border-cyan-500/40 bg-cyan-500/10 px-3 py-2 text-xs font-semibold text-cyan-100 transition-colors hover:border-cyan-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
            disabled={isAttaching}
          >
            Add Company
          </button>
        ) : (
          <button
            type="button"
            onClick={handleClose}
            className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200"
          >
            Cancel
          </button>
        )}
      </div>

      {isOpen ? (
        <div className="mt-4 space-y-4">
          <div className="flex flex-col gap-3 sm:flex-row">
            <input
              type="text"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search company"
              className="min-w-0 flex-1 rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 placeholder:text-slate-500"
              aria-label="Search company"
            />
            {isSearching ? (
              <div className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-300">
                Searching...
              </div>
            ) : null}
          </div>

          {searchResults.length ? (
            <div className="max-h-56 space-y-2 overflow-y-auto rounded-xl border border-slate-800 bg-slate-950/60 p-2">
              {searchResults.map((company) => {
                const isSelected = selectedCompany?._record_id === company._record_id;
                return (
                  <button
                    key={company._record_id}
                    type="button"
                    onClick={() => {
                      setSelectedCompany(company);
                      setShowConfirm(false);
                    }}
                  className={`w-full rounded-lg border px-3 py-2 text-left transition-colors ${
                      isSelected
                        ? "border-cyan-400/50 bg-cyan-500/10"
                        : "border-slate-800 bg-slate-950/60 hover:border-slate-600"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-slate-100">{company.ai_name}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}

          {selectedCompany ? (
            <div className="rounded-xl border border-slate-800 bg-slate-950/60 p-3">
              <p className="text-sm font-semibold text-slate-100">Selected company</p>
              <p className="mt-1 text-sm text-slate-300">{selectedCompany.ai_name}</p>
              <div className="mt-3 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedCompany(null)}
                  className="rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-semibold text-slate-200"
                >
                  Clear
                </button>
                {!showConfirm ? (
                  <button
                    type="button"
                    onClick={() => setShowConfirm(true)}
                    className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100 transition-colors hover:border-amber-400 hover:text-white"
                    disabled={isAttaching}
                  >
                    Merge
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={handleAttach}
                    className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-100 transition-colors hover:border-amber-400 hover:text-white disabled:cursor-not-allowed disabled:opacity-40"
                    disabled={isAttaching}
                  >
                    {isAttaching ? "Merging..." : "Confirm Merge"}
                  </button>
                )}
              </div>
              {showConfirm ? (
                <p className="mt-3 text-xs text-slate-400">
                  Confirming will attach the selected documents to this company and add back-links to each document.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}

      {localError || error ? (
        <div className="mt-4 rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">
          {localError || error}
        </div>
      ) : null}
    </div>
  );
};

export default CompanyAttachActions;
